import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Play, Pause, LoaderCircle } from 'lucide-react';
import analyticsService from '../services/analyticsService';
import { useTranslate } from '../lib/i18n';

interface Props {
  url: string;
  onTimeUpdate?: (time: number) => void;
  onEnded?: () => void;
  onMount?: () => void;
  onUnmount?: () => void;
}

const AudioPlayer = forwardRef(({ url, onTimeUpdate, onEnded, onMount, onUnmount }: Props, ref) => {
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const playStartTimeRef = useRef<number | null>(null); // added ref to track play start time
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const t = useTranslate();

  const seekTo = (time: number) => {
    if (audioRef.current && !isLoading && !error) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
      onTimeUpdate?.(time);
    }
  };

  useImperativeHandle(ref, () => ({
    seekTo,
  }));

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    return () => {
      audio.src = '';
    };
  }, [url]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadStart = () => setIsLoading(true);

    const handleLoadedData = () => {
      setIsLoading(false);
      setDuration(audio.duration);
      setError(null);
      onMount?.();
    };

    const handleError = () => {
      setIsLoading(false);
      setError(t('audioPlayer.failedToLoad'));
      setIsPlaying(false);
    };

    const handleLoadedMetadata = () => {
      if (audio.duration) {
        setDuration(audio.duration);
      }
      setIsLoading(false);
    };

    const handlePlayEvent = () => {
      setIsPlaying(true);
    };

    const handlePauseEvent = () => {
      setIsPlaying(false);
    };

    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('loadeddata', handleLoadedData);
    audio.addEventListener('error', handleError);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('play', handlePlayEvent);
    audio.addEventListener('pause', handlePauseEvent);

    return () => {
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('loadeddata', handleLoadedData);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('play', handlePlayEvent);
      audio.removeEventListener('pause', handlePauseEvent);
      onUnmount?.();
    };
  }, [onMount, onUnmount, t]);

  const handlePlay = () => {
    analyticsService.trackEvent({
      eventName: 'playButtonClicked',
      properties: { audioUrl: url },
    });
    playStartTimeRef.current = Date.now(); // record play start time
    setIsPlaying(true);

    if (audioRef.current) {
      audioRef.current.play().catch(() => {
        setError(t('audioPlayer.failedToPlay'));
        setIsPlaying(false);
      });
    }
  };

  const handlePause = () => {
    if (!audioRef.current) return;

    audioRef.current.pause();
    setIsPlaying(false);
    playStartTimeRef.current = null;
  };

  const togglePlay = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (error) {
      audioRef.current?.load();
      return;
    }

    if (isPlaying) {
      handlePause();
    } else {
      handlePlay();
    }
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    setCurrentTime(audioRef.current.currentTime);
    onTimeUpdate?.(audioRef.current.currentTime);
  };

  const handleSeek = (e: React.MouseEvent) => {
    if (isLoading || error) return;

    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const position = (e.clientX - rect.left) / rect.width;
    const newTime = position * duration;

    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }

    setCurrentTime(newTime);
    onTimeUpdate?.(newTime);
  };

  const formatTime = (time: number): string => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (error) return null;

  return (
    <div className="relative z-1 px-4 py-2">
      <div className="mb-2 flex items-center justify-between" style={{ minHeight: '32px' }}>
        <h2 className="text-sm">{t('audioPlayer.listenToContent')}</h2>
        <button
          onClick={togglePlay}
          disabled={isLoading}
          className="flex h-8 w-8 items-center justify-center rounded-full bg-foreground"
          style={{ opacity: isLoading ? 0.7 : 1 }}
        >
          {isLoading ? (
            <LoaderCircle className="animate-spin text-primary" size={16} />
          ) : isPlaying ? (
            <Pause className="text-primary" size={16} fill="currentColor" />
          ) : (
            <Play className="ml-0.5 text-primary" size={16} fill="currentColor" />
          )}
        </button>
      </div>
      <div className="flex items-center gap-2" style={{ minHeight: '16px' }}>
        <span className="text-xs text-muted-foreground">{formatTime(currentTime)}</span>
        <div
          className="h-1 flex-1 cursor-pointer rounded-full bg-muted"
          onClick={handleSeek}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
              e.preventDefault();
              if (isLoading || error) return;

              const skipTime = 5; // Skip 5 seconds
              const newTime =
                e.key === 'ArrowLeft'
                  ? Math.max(0, currentTime - skipTime)
                  : Math.min(duration, currentTime + skipTime);

              if (audioRef.current) {
                audioRef.current.currentTime = newTime;
              }
              setCurrentTime(newTime);
              onTimeUpdate?.(newTime);
            }
          }}
          role="progressbar"
          tabIndex={0}
          style={{ opacity: isLoading ? 0.5 : 1 }}
        >
          <div
            className="h-1 rounded-full bg-primary"
            style={{
              width: `${(currentTime / duration) * 100}%`,
              transition: isLoading ? 'none' : 'width 0.1s linear',
            }}
          />
        </div>
        <span className="text-xs text-muted-foreground">{formatTime(duration)}</span>
      </div>
      <audio
        ref={audioRef}
        src={url}
        onTimeUpdate={handleTimeUpdate}
        onEnded={() => {
          setIsPlaying(false);
          playStartTimeRef.current = null;
          onEnded?.();
        }}
      />
    </div>
  );
});

AudioPlayer.displayName = 'AudioPlayer';

export default AudioPlayer;
